import React from 'react';
import { Box, Card, Chip, Divider, FormControlLabel, Radio, Typography } from '@mui/material';
import SchoolTwoToneIcon from '@mui/icons-material/SchoolTwoTone';

import { buildCampusFeeBreakdown } from '../../utils/campusFeeBreakdown';
import {
    CAMPUS_PAYMENT_FULL,
    CAMPUS_PAYMENT_MINIMUM,
    amountsForCampusOptions,
    campusPaymentKey,
} from '../../utils/campusPaymentOptions';

const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const childLabel = (count) => `${count} ${count === 1 ? 'child' : 'children'}`;

const optionCardSx = (selected) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    m: 0,
    p: 1.25,
    border: selected ? '1px solid #bae6fd' : '1px solid #e2e8f0',
    borderRadius: '10px',
    bgcolor: selected ? '#f0f9ff' : '#ffffff',
    '&:hover': { bgcolor: selected ? '#f0f9ff' : '#f8fafc' },
    '& .MuiFormControlLabel-label': { flex: 1, width: '100%', minWidth: 0 },
});

/**
 * Amount due at each campus, shown before the parent enters payment details.
 * Only rendered for multi-campus families. Figures come from /calculatefees:
 * the family discount is applied across all children first, then grouped by
 * campus — the same amounts the gateway charges.
 *
 * Multi-campus families also choose, per campus, the re-enrolment minimum or
 * the full amount, unless they selected Early Bird (original full-payment flow).
 */
const CampusFeeBreakdown = ({
    students,
    enrolledChildren,
    campusBreakdown,
    fallbackCampus,
    amountToPay,
    title = 'Payment Breakdown by Campus',
    showPaymentOptions = false,
    campusPaymentSelections = {},
    onPaymentModeChange,
    paymentOptionCopy,
}) => {
    const { campuses, totals } = buildCampusFeeBreakdown({
        students,
        children: enrolledChildren,
        campusBreakdown,
        fallbackCampus,
        amountToPay,
    });

    if (campuses.length < 2) return null;

    const anyDiscount = totals.discount > 0;
    const copy = paymentOptionCopy || {
        [CAMPUS_PAYMENT_MINIMUM]: {
        title: 'Pay the minimum required for re-enrolment',
        description: 'Pay only the deposit and resource fees required to complete re-enrolment.',
    },
        [CAMPUS_PAYMENT_FULL]: {
            title: 'Pay the full amount',
            description: 'Pay this campus’s full fees now, including resource fees.',
        },
    };

    return (
        <Card
            sx={{
                mb: 4,
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
            }}
        >
            <Box sx={{ bgcolor: '#f8fafc', p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '18px' }}>
                    {title}
                </Typography>
                <Typography sx={{ color: '#64748b', fontSize: '13px', mt: 0.5 }}>
                    {showPaymentOptions
                        ? 'Choose how much to pay at each campus. Family discounts are applied across all your children first, then shown on the campus each child attends.'
                        : 'Amount due at each campus after the family discount has been applied across all your children.'}
                    {!showPaymentOptions && anyDiscount
                        ? ' The discount is calculated for the whole family, then shown on the campus each child attends.'
                        : ''}
                </Typography>
            </Box>

            <Box>
                {campuses.map((campus, index) => {
                    const campusKey = campusPaymentKey(campus) || campus.college;
                    const selectedMode = campusPaymentSelections[campusKey]
                        || campus.paymentMode
                        || CAMPUS_PAYMENT_MINIMUM;
                    const optionAmounts = amountsForCampusOptions(campus);
                    const showOptions = showPaymentOptions && !campus.isPaid;

                    return (
                        <Box key={campus.college || campusKey}>
                            {index > 0 && <Divider sx={{ mx: 3 }} />}
                            <Box
                                sx={{
                                    px: 3,
                                    py: 2,
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    justifyContent: 'space-between',
                                    alignItems: { xs: 'flex-start', sm: showOptions ? 'flex-start' : 'center' },
                                    gap: 1.5,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                    <SchoolTwoToneIcon sx={{ color: '#059669', fontSize: 22 }} />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ color: '#0f172a', fontWeight: 600, fontSize: '15px' }}>
                                            {campus.college}
                                        </Typography>
                                        <Typography sx={{ color: '#64748b', fontSize: '13px' }}>
                                            {childLabel(campus.childCount)}
                                            {campus.discount > 0 ? ` · discount applied ${money(campus.discount)}` : ''}
                                            {campus.DiscountOption ? ` · ${campus.DiscountOption}` : ''}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                                    <Typography
                                        sx={{
                                            color: campus.isPaid ? '#065f46' : '#0f172a',
                                            fontWeight: 700,
                                            fontSize: '16px',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {money(campus.amountDue)}
                                    </Typography>
                                    {campus.isPaid ? (
                                        <Chip
                                            label={`Paid ${money(campus.alreadyPaidAmount || campus.amountDue)}`}
                                            size="small"
                                            sx={{
                                                mt: 0.5,
                                                height: '22px',
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                color: '#065f46',
                                                bgcolor: '#ecfdf5',
                                                border: '1px solid #6ee7b7',
                                            }}
                                        />
                                    ) : (
                                        campus.creditApplied > 0 && (
                                            <Typography sx={{ color: '#047857', fontSize: '12px', mt: 0.25 }}>
                                                {`Credit ${money(campus.creditApplied)} · card ${money(campus.cardAmount)}`}
                                            </Typography>
                                        )
                                    )}
                                </Box>
                            </Box>

                            {showOptions && (
                                <Box
                                    role="radiogroup"
                                    aria-label={`Payment options for ${campus.college}`}
                                    sx={{
                                        px: 3,
                                        pb: 2.5,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                    }}
                                >
                                    {[CAMPUS_PAYMENT_MINIMUM, CAMPUS_PAYMENT_FULL].map((mode) => {
                                        const selected = selectedMode === mode;
                                        const amount = mode === CAMPUS_PAYMENT_MINIMUM
                                            ? optionAmounts.minimumAmount
                                            : optionAmounts.fullAmount;
                                        return (
                                            <FormControlLabel
                                                key={mode}
                                                value={mode}
                                                control={
                                                    <Radio
                                                        checked={selected}
                                                        onChange={() => onPaymentModeChange?.(campusKey, mode, campus)}
                                                        sx={{ color: '#64748b', '&.Mui-checked': { color: '#0ea5e9' }, pt: 0.5 }}
                                                    />
                                                }
                                                label={
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, width: '100%' }}>
                                                        <Box>
                                                            <Typography sx={{ color: '#0f172a', fontWeight: 600, fontSize: '14px' }}>
                                                                {copy[mode].title}
                                                            </Typography>
                                                            <Typography sx={{ color: '#64748b', fontSize: '12px', mt: 0.25 }}>
                                                                {copy[mode].description}
                                                            </Typography>
                                                        </Box>
                                                        <Typography sx={{ color: '#0f172a', fontWeight: 700, fontSize: '14px', whiteSpace: 'nowrap' }}>
                                                            {money(amount)}
                                                        </Typography>
                                                    </Box>
                                                }
                                                sx={optionCardSx(selected)}
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    );
                })}

                <Box
                    sx={{
                        px: 3,
                        py: 2.5,
                        bgcolor: '#f1f5f9',
                        borderTop: '1px solid #cbd5e1',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography sx={{ color: '#334155', fontSize: '16px', fontWeight: 600 }}>
                            Total to pay now, all campuses
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '12px' }}>
                            {`${childLabel(totals.childCount)} across ${campuses.length} campuses`}
                        </Typography>
                        {/*
                          Only figures that add up to the total beside them.
                          
                          This briefly also showed the year's school fees and the discount taken off
                          them. Both were true and neither belonged here: the total on this card is
                          what is due *now*, so a reader saw $6,500 less $200 and then $1,250, and
                          three correct numbers made a wrong impression. The year's fees and the
                          discount are on the fee card above, where the total they reconcile with is.
                        */}
                        {totals.creditApplied > 0 && (
                            <Typography sx={{ color: '#047857', fontSize: '12px', mt: 0.5 }}>
                                {`${money(totals.creditApplied)} from credit · ${money(totals.cardAmount)} on your card`}
                            </Typography>
                        )}
                    </Box>
                    <Typography
                        sx={{ color: '#0f172a', fontWeight: 700, fontSize: '18px', whiteSpace: 'nowrap' }}
                    >
                        {money(totals.amountDue)}
                    </Typography>
                </Box>
            </Box>
        </Card>
    );
};

export default CampusFeeBreakdown;
